import { Alert, Box, Button, Grid, Group, Modal, Radio, Stack, Text, Textarea, TextInput } from '@mantine/core'
import { MIME_TYPES } from '@mantine/dropzone'
import { IconDeviceFloppy, IconExternalLink, IconInfoCircle } from '@tabler/icons-react'
import type { MRT_ColumnDef } from 'mantine-react-table'
import { useEffect, useRef, useState } from 'react'
import type { z } from 'zod'

import { AIInputWrapper } from '@/components/AIInputWrapper'
import type { FileUploadProps } from '@/components/FileUpload'
import {
  acceptedFileUploadText,
  ALLOWED_IMAGE_TYPES,
  contentUploadMimeTypes,
  FileUpload,
  needsExtractionMimeTypes,
  supportsProgressMimeTypes,
} from '@/components/FileUpload'
import { RadioCard } from '@/components/RadioCard'
import { Table } from '@/components/Table'
import { getResourceFieldConfigByType } from '@/configs/aiFieldConfigs'
import { theme } from '@/configs/theme'
import { useAIFieldGeneration } from '@/hooks/useAIFieldGeneration'
import { useFileUpload } from '@/hooks/useFileUpload'
import { useGlobalState } from '@/hooks/useGlobalState'
import type { CMFormSection } from '@/screens/ContentModuleFormScreen/ContentModuleFormScreen'
import type { ResourceDataContentModalProps } from '@/screens/ContentModuleFormScreen/ResourceDataContentModal'
import { ResourceDataContentModal } from '@/screens/ContentModuleFormScreen/ResourceDataContentModal'
import type { ProcessProgress } from '@/services/api/api.types'
import * as commonClasses from '@/styles/componentCommons.css'
import * as classes from '@/styles/ContentModuleFormScreen.css'
import type { ResourceFormSchema } from '@/utils/contentModuleForm'
import { castZodFormToResource, resourceSchema } from '@/utils/contentModuleForm'
import { getRequiredFieldDescriptionProps } from '@/utils/forms'

type ResourceZodType = z.infer<ResourceFormSchema>

export type ResourceModalProps = {
  type: 'cm' | 'step'
}

const fileUploadProps: Record<NonNullable<ResourceZodType>['type'], Partial<FileUploadProps>> = {
  text: {
    titles: { idle: 'Upload File' },
    accept: contentUploadMimeTypes,
    primaryActionLabel: 'Select File',
  },
  url: {},
  video: {
    titles: { idle: 'Upload Video' },
    accept: [MIME_TYPES.mp4],
    primaryActionLabel: 'Select Video',
  },
  image: {
    titles: { idle: 'Upload Image' },
    accept: [...ALLOWED_IMAGE_TYPES],
    primaryActionLabel: 'Select Image',
  },
}

export function ResourceModal(props: CMFormSection & ResourceModalProps) {
  const { configStore } = useGlobalState()

  // State to track processing of any uploaded content
  const [processing, setProcessing] = useState<boolean>(false)
  const [processProgressPct, setProcessProgressPct] = useState<number>(0)

  // Track if we've already triggered auto-generation to avoid re-triggering
  const autoGenerationTriggeredRef = useRef<string | null>(null)

  // Check if this is a new resource and if we should show the text option
  const isNewResource = !props.resourceId
  const shouldShowTextOption = !isNewResource && props.resourceForm.getValues().type === 'text'

  // Check if the resource needs approval
  const needsApproval = props.resourceForm.getValues().isApproved === false

  // Track individual field approvals
  const [titleApproved, setTitleApproved] = useState<boolean>(!needsApproval)
  const [descriptionApproved, setDescriptionApproved] = useState<boolean>(!needsApproval)

  const { uploading, upload } = useFileUpload({
    onPrepareError: () =>
      props.resourceForm.setFieldError('file', 'Something went wrong. Please try again or contact support.'),
    onUploadError: () =>
      props.resourceForm.setFieldError('file', 'Upload failed. Please try again or contact support.'),
    onUploadSuccess: () => props.resourceForm.setFieldError('file', undefined),
  })

  const { getFieldState, generateFieldWithConfig, rejectSuggestion } = useAIFieldGeneration(props)

  // Helper to generate metadata using the appropriate endpoint based on resource type
  const generateMetadataForField = async (fieldName: 'title' | 'description') => {
    const resourceType = props.resourceForm.getValues().type
    const config = getResourceFieldConfigByType(resourceType, fieldName)

    if (config) {
      generateFieldWithConfig('resource', config)
    } else {
      console.warn(`No AI config found for ${resourceType}.${fieldName}`)
    }
  }

  const regenerateMetadataForField = async (fieldName: 'title' | 'description') => {
    const resourceType = props.resourceForm.getValues().type
    const config = getResourceFieldConfigByType(resourceType, fieldName)

    if (config) {
      generateFieldWithConfig('resource', config)
    } else {
      console.warn(`No AI config found for ${resourceType}.${fieldName}`)
    }
  }

  // Custom accept handlers to ensure proper form updates
  const acceptTitle = () => {
    const fieldState = getFieldState('title')
    if (fieldState.state === 'pending' && fieldState.suggestion) {
      props.resourceForm.setFieldValue('title', fieldState.suggestion)
      rejectSuggestion('title') // Clear the AI state after updating
    }
    // Mark title as approved
    if (needsApproval) {
      setTitleApproved(true)
    }
  }

  const acceptDescription = () => {
    const fieldState = getFieldState('description')
    if (fieldState.state === 'pending' && fieldState.suggestion) {
      props.resourceForm.setFieldValue('description', fieldState.suggestion)
      rejectSuggestion('description') // Clear the AI state after updating
    }
    // Mark description as approved
    if (needsApproval) {
      setDescriptionApproved(true)
    }
  }

  // When both fields are approved, set isApproved to true
  useEffect(() => {
    if (needsApproval && titleApproved && descriptionApproved) {
      props.resourceForm.setFieldValue('isApproved', true)
    }
  }, [titleApproved, descriptionApproved, needsApproval, props.resourceForm])

  function openResourceContentModal() {
    props.openModal?.({
      title: `${!props.resourceForm.getValues().data ? 'New' : 'Edit'} Resource Content Entry`,
      subtitle: 'Copy and paste or enter your content manually.',
      Component: ResourceDataContentModal,
      ComponentProps: { onSubmit: submitResource } satisfies ResourceDataContentModalProps,
    })
  }

  async function validateAndSubmitResource() {
    const values = props.resourceForm.getValues()
    const validate = props.resourceForm.validateField

    const validations = [
      validate('type'),
      props.resourceId && validate('title'),
      props.resourceId && validate('description'),
      values.type === 'url' && validate('dataUrl'),
      values.type === 'text' && (values.file ? validate('file') : validate('data')),
      (values.type === 'image' || values.type === 'video') && validate('data'),
    ].filter(Boolean)

    if (validations.some(v => v && v.hasError)) {
      return
    }

    // Check if approval is required before saving (only if AI is enabled)
    if (needsApproval && !isAIDisabled) {
      return // Don't submit if not approved
    }

    await submitResource()

    if (props.resourceId) {
      props.closeModal()
    }
  }

  function onProgress(progress: ProcessProgress['processProgress']) {
    const current = progress?.current ?? 0
    const total = progress?.total ?? 0
    // Avoid NaN on divide by zero
    const fraction = current / total || 0
    const progressPct = fraction * 100

    console.log(`ResourceModal:onProgress ${progressPct}`)
    setProcessProgressPct(progressPct)
  }

  async function submitResource() {
    const values = props.resourceForm.getValues()
    const file = values.file

    // This is passed as a form field from the previous step, which may have uploaded this file.
    // We need to remove it from the params so it's not included in the mutation.
    if (file) {
      values.file = null
    }

    // Automatically approve resources when AI is disabled (e.g., URL resources)
    if (isAIDisabled) {
      values.isApproved = true
      props.resourceForm.setFieldValue('isApproved', true)
    }

    const idParams = props.type === 'cm' ? { task_id: props.taskId! } : { step_id: props.stepId! }
    const params = castZodFormToResource(values)

    if (props.resourceId) {
      // update
      if (needsExtractionMimeTypes.includes(file?.type ?? '')) {
        setProcessing(true)
        setProcessProgressPct(0)

        if (supportsProgressMimeTypes.includes(file?.type ?? '')) {
          await props.resourceUpdateMutation?.mutateAsync({
            ...params,
            ...idParams,
            resource_id: props.resourceId,
            onProgress,
          })
        } else {
          // We fake our processing progress for filetypes that don't support progress feedback
          // in the processing backend
          setProcessProgressPct(50)
          await props.resourceUpdateMutation?.mutateAsync({ ...params, ...idParams, resource_id: props.resourceId })
        }

        setProcessing(false)
      } else {
        await props.resourceUpdateMutation?.mutateAsync({ ...params, ...idParams, resource_id: props.resourceId })
      }
    } else {
      // create
      if (needsExtractionMimeTypes.includes(file?.type ?? '')) {
        setProcessing(true)
        setProcessProgressPct(0)

        let response
        if (supportsProgressMimeTypes.includes(file?.type ?? '')) {
          response = await props.resourceCreateMutation?.mutateAsync({ ...params, ...idParams, onProgress })
        } else {
          // We fake our processing progress for filetypes that don't support progress feedback
          // in the processing backend
          setProcessProgressPct(50)
          response = await props.resourceCreateMutation?.mutateAsync({ ...params, ...idParams })
        }

        setProcessing(false)

        // Update the form with the new resource ID
        if (response?.resourceId) {
          props.resourceForm.setFieldValue('resourceId', response.resourceId)
        }
      } else {
        const response = await props.resourceCreateMutation?.mutateAsync({ ...params, ...idParams })
        if (response?.resourceId) {
          props.resourceForm.setFieldValue('resourceId', response.resourceId)
        }
      }
    }
  }

  const isMutating = props.resourceCreateMutation?.isPending || props.resourceUpdateMutation?.isPending
  const hasData =
    props.resourceForm.getValues().data || props.resourceForm.getValues().dataUrl || props.resourceForm.getValues().file
  // AI generation is disabled for URL resources (no file to analyze) but enabled for uploaded videos/images
  const isAIDisabled = props.resourceForm.getValues().type === 'url'

  // Reset auto-generation tracking when resourceId changes (different resource)
  useEffect(() => {
    autoGenerationTriggeredRef.current = null
  }, [props.resourceId])

  // Auto-generate AI content for empty fields when content becomes available
  useEffect(() => {
    const hasResourceId = !!props.resourceId
    const hasContent = hasData
    const shouldTrigger = props.resourceCreateMutation?.isSuccess || props.resourceUpdateMutation?.isSuccess

    // Get current form values to detect when form has been updated
    const formValues = props.resourceForm.getValues()
    const hasFormData = hasResourceId && (formValues.data || formValues.dataUrl)

    // Create a unique key for this resource+content combination
    const generationKey = `${props.resourceId}-${hasContent}-${hasFormData}`

    // Auto-generate AI suggestions when:
    // 1. We have a resourceId and content is available
    // 2. AI is not disabled for this resource type (only disabled for YouTube URLs)
    // 3. This is triggered by resource creation or update OR needs approval with blank fields
    // 4. The form has been populated with resource data
    // 5. We haven't already triggered auto-generation for this resource+content combination
    const shouldAutoGenerate = shouldTrigger || (needsApproval && hasResourceId && hasFormData)

    if (
      hasResourceId &&
      hasContent &&
      hasFormData &&
      !isAIDisabled &&
      shouldAutoGenerate &&
      autoGenerationTriggeredRef.current !== generationKey
    ) {
      autoGenerationTriggeredRef.current = generationKey

      // Check which fields are empty and trigger AI generation
      if (!formValues.title?.trim()) {
        generateMetadataForField('title')
      }

      if (!formValues.description?.trim()) {
        generateMetadataForField('description')
      }
    }
  }, [
    props.resourceId,
    hasData,
    isAIDisabled,
    needsApproval,
    props.resourceCreateMutation?.isSuccess,
    props.resourceUpdateMutation?.isSuccess,
    generateMetadataForField,
    // Watch for form value changes that indicate the form was updated
    props.resourceForm.getValues().resourceId,
    props.resourceForm.getValues().data,
    props.resourceForm.getValues().dataUrl,
  ])

  return (
    <>
      <Box className={commonClasses.stickyModalFooterContentWrapper}>
        {needsApproval && (
          <Alert variant="light" color="blue" icon={<IconInfoCircle />} mb="xl">
            <Text size="sm" fw={500}>
              This resource contains AI-generated content that requires your approval before saving.
            </Text>
          </Alert>
        )}
        <Grid gutter="xxxl">
          <Grid.Col span={12}>
            <Radio.Group
              withAsterisk
              label="Type"
              description="The type determines how the resource will be used."
              {...props.resourceForm.getInputProps('type')}
              onChange={e => {
                props.resourceForm.setFieldValue('type', e as z.infer<ResourceFormSchema>['type'])

                props.resourceForm.setFieldValue('data', '')
                props.resourceForm.setFieldValue('dataUrl', '')
                props.resourceForm.setFieldValue('file', null)

                props.resourceForm.setFieldError('data', '')
                props.resourceForm.setFieldError('dataUrl', '')
                props.resourceForm.setFieldError('file', '')

                props.metadataForm.setFieldValue('resource', undefined)
              }}>
              <Group justify="space-between" gap="xl" align="stretch">
                {shouldShowTextOption && (
                  <RadioCard
                    label="Additional Information"
                    description="Sidekick will commit this information to memory and share with users when appropriate."
                    value={resourceSchema.shape.type.enum.text}
                  />
                )}
                <RadioCard
                  label="Supplemental URL"
                  description="Sidekick will display this website for users when appropriate."
                  value={resourceSchema.shape.type.enum.url}
                />
                <RadioCard
                  label="Supplemental Video"
                  description="Sidekick will play this video file for users when appropriate."
                  value={resourceSchema.shape.type.enum.video}
                />
                <RadioCard
                  label="Supplemental Image"
                  description="Sidekick will display this image file for users when appropriate."
                  value={resourceSchema.shape.type.enum.image}
                />
              </Group>
            </Radio.Group>
          </Grid.Col>

          <Grid.Col span={12}>
            {props.resourceForm.getValues().type === 'url' ? (
              <TextInput
                withAsterisk
                label="URL"
                description="The URL of the resource."
                {...props.resourceForm.getInputProps('dataUrl')}
                {...getRequiredFieldDescriptionProps(props.resourceForm, 'dataUrl', 'The URL of the resource.')}
              />
            ) : (
              <FileUpload
                withAsterisk
                label="Resource File"
                activateOnClick={false}
                loading={uploading || isMutating}
                processing={processing}
                processProgress={processProgressPct}
                maxFiles={1}
                error={
                  props.resourceForm.getValues().type !== 'url'
                    ? props.resourceForm.getInputProps('file').error || props.resourceForm.getInputProps('data').error
                    : undefined
                }
                {...fileUploadProps[props.resourceForm.getValues().type ?? 'text']}
                secondaryActionLabel={props.resourceForm.getValues().type === 'text' ? 'Enter Manually' : undefined}
                onSecondaryActionClick={openResourceContentModal}
                hints={{
                  idle: (
                    <span style={{ whiteSpace: 'pre-line' }}>
                      {props.resourceForm.getValues().type === 'text'
                        ? acceptedFileUploadText
                        : `Drag and drop media files here or click below to browse.`}
                    </span>
                  ),
                }}
                onDrop={async files => {
                  props.resourceForm.setFieldValue('file', null)
                  props.resourceForm.setFieldValue('data', '')
                  props.resourceForm.setFieldValue('fileName', '')

                  props.resourceForm.setFieldError('file', '')
                  props.resourceForm.setFieldError('data', '')

                  const file = files[0]

                  if (props.resourceForm.getValues().type === 'text') {
                    const uploadResponse = await upload(file)
                    if (!uploadResponse) return

                    // FIXME: we add this here so we can see the mimetype later when determining
                    // if we can track progress for this file. But it needs to be deleted from the params
                    // for the mutation, having already been uploaded. This mechanism needs to be simplified
                    // and shared across the various pieces of upload UI.
                    props.resourceForm.setFieldValue('file', file)
                    props.resourceForm.setFieldValue('s3File', uploadResponse.fileName)
                    props.resourceForm.setFieldValue('fileName', file.name)
                  } else {
                    const uploadResponse = await upload(file)
                    if (!uploadResponse) return

                    props.resourceForm.setFieldValue('data', uploadResponse.fileName)
                    props.resourceForm.setFieldValue('fileName', file.name)
                  }

                  void submitResource()
                }}
                BottomSlot={
                  hasData && (
                    <Table
                      data={hasData ? [props.resourceForm.getValues()] : []}
                      columns={contentCols}
                      rowActionsInclude={row => {
                        if (row.original.type === 'text') {
                          return ['edit']
                        } else {
                          return ['view']
                        }
                      }}
                      enableTopToolbar={false}
                      enableTableHead={false}
                      mantinePaperProps={{ className: classes.fileUploadTable }}
                      onRowActionClick={(action, row) => {
                        if (action === 'edit') {
                          openResourceContentModal()
                        } else if (action === 'view') {
                          window.open(`${configStore.serverBaseUrl}/files/${row.original.data}`, '_blank')
                        }
                      }}
                      displayColumnDefOptions={{ 'mrt-row-actions': { size: 60 } }}
                    />
                  )
                }
              />
            )}
            {(uploading || isMutating || processing) && (
              <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }} mt="sm">
                <Text c="dimmed" className={classes.inputDescription}>
                  {uploading && 'Uploading content ...'}
                  {processing && 'Processing content, this may take a few minutes...'}
                  {!processing && !uploading && 'Loading content...'}
                </Text>
              </Box>
            )}
          </Grid.Col>

          {!!props.resourceId && (
            <>
              <Grid.Col span={6}>
                <Stack gap="xxxl">
                  <AIInputWrapper
                    disabled={isAIDisabled}
                    fieldState={
                      needsApproval && !titleApproved && props.resourceForm.getValues().title?.trim()
                        ? { state: 'pending', suggestion: props.resourceForm.getValues().title }
                        : getFieldState('title')
                    }
                    onAskSidekick={async () => {
                      if (props.resourceForm.isDirty('dataUrl')) {
                        await submitResource()
                        props.resourceForm.resetDirty()
                      }
                      await generateMetadataForField('title')
                    }}
                    onAccept={acceptTitle}
                    onReject={() => rejectSuggestion('title')}
                    onRegenerate={async () => {
                      if (props.resourceForm.isDirty('dataUrl')) {
                        await submitResource()
                        props.resourceForm.resetDirty()
                      }
                      await regenerateMetadataForField('title')
                    }}>
                    <Textarea
                      withAsterisk
                      label="Title"
                      autosize
                      resize="vertical"
                      flex={1}
                      classNames={{
                        input: theme.cx(classes.resourceDescriptionInput),
                      }}
                      {...props.resourceForm.getInputProps('title')}
                      {...getRequiredFieldDescriptionProps(props.resourceForm, 'title', 'Title of the resource.')}
                    />
                  </AIInputWrapper>

                  {/* <TagsInput
                    allowDuplicates
                    label="Keywords"
                    placeholder="Type keyword and press Enter..."
                    description="Keywords help the system know when to recommend this resource."
                    {...props.resourceForm.getInputProps('keywords')}
                  /> */}
                </Stack>
              </Grid.Col>

              <Grid.Col span={6}>
                <AIInputWrapper
                  disabled={isAIDisabled}
                  fieldState={
                    needsApproval && !descriptionApproved && props.resourceForm.getValues().description?.trim()
                      ? { state: 'pending', suggestion: props.resourceForm.getValues().description }
                      : getFieldState('description')
                  }
                  onAskSidekick={async () => {
                    if (props.resourceForm.isDirty('dataUrl')) {
                      await submitResource()
                      props.resourceForm.resetDirty()
                    }
                    await generateMetadataForField('description')
                  }}
                  onAccept={acceptDescription}
                  onReject={() => rejectSuggestion('description')}
                  onRegenerate={async () => {
                    if (props.resourceForm.isDirty('dataUrl')) {
                      await submitResource()
                      props.resourceForm.resetDirty()
                    }
                    await regenerateMetadataForField('description')
                  }}>
                  <Textarea
                    withAsterisk
                    label="Description"
                    autosize
                    resize="vertical"
                    flex={1}
                    classNames={{
                      input: theme.cx(classes.resourceDescriptionInput),
                    }}
                    {...props.resourceForm.getInputProps('description')}
                    {...getRequiredFieldDescriptionProps(
                      props.resourceForm,
                      'description',
                      'Brief description of file content.',
                    )}
                  />
                </AIInputWrapper>
              </Grid.Col>
            </>
          )}
        </Grid>
      </Box>

      <Modal.Header className={commonClasses.stickyModalFooter}>
        <Group gap="xl">
          {!props.resourceId && props.resourceForm.getValues().type === 'url' ? (
            <Button
              leftSection={<IconDeviceFloppy />}
              loading={isMutating}
              onClick={validateAndSubmitResource}
              disabled={needsApproval && !isAIDisabled}>
              Save Resource URL
            </Button>
          ) : props.resourceId ? (
            <Button
              leftSection={<IconDeviceFloppy />}
              loading={isMutating}
              onClick={validateAndSubmitResource}
              disabled={needsApproval && !isAIDisabled}>
              Save Supplemental Resource
            </Button>
          ) : null}

          {!isMutating && (
            <Button size="sm" variant="light" color="gray" onClick={() => props.closeModal()}>
              Cancel
            </Button>
          )}
        </Group>
      </Modal.Header>
    </>
  )
}

const contentCols: MRT_ColumnDef<NonNullable<ResourceZodType>>[] = [
  {
    id: 'content',
    header: '',
    grow: true,
    Cell: props => {
      const { configStore } = useGlobalState()
      const { type } = props.row.original

      if (type === 'text') {
        return (
          <Text size="sm" lineClamp={2} style={{ whiteSpace: 'pre-wrap' }}>
            {props.row.original.data}
          </Text>
        )
      } else {
        return (
          <Group gap="xl">
            <Button
              rightSection={<IconExternalLink size={16} />}
              color="indigo.3"
              variant="filled"
              component="a"
              size="xs"
              tt="uppercase"
              target="_blank"
              href={`${configStore.serverBaseUrl}/files/${props.row.original.data}`}>
              Preview {type}
            </Button>

            <Text size="sm" fw={500} lineClamp={2}>
              {props.row.original.fileName}
            </Text>
          </Group>
        )
      }
    },
  },
]

// Removed per Jeff's request
// {props.resourceForm.getValues().type !== 'text' && (
//   <>
//     <Grid.Col span={6}>
//       <Textarea
//         withAsterisk
//         label={<ComingSoon prefix="ESK Intro " />}
//         description="This is how the file will be introduced to the ESK."
//         autosize
//         resize="vertical"
//         flex={1}
//         minRows={8}
//         rightSectionProps={{ className: commonStyles.textareaRightSectionWithAIActionButton }}
//         rightSection={
//           <AIActionButton
//             variant="action-button"
//             onClick={() => chatStore.submitMockSMESKChatFn?.('This feature is coming soon!')}
//           />
//         }
//       />
//     </Grid.Col>

//     <Grid.Col span={6}>
//       <Textarea
//         label={<ComingSoon prefix="Purpose " />}
//         description="The purpose of the file and how it will be used."
//         autosize
//         resize="vertical"
//         flex={1}
//         minRows={8}
//         rightSectionProps={{ className: commonStyles.textareaRightSectionWithAIActionButton }}
//         rightSection={
//           <AIActionButton
//             variant="action-button"
//             onClick={() => chatStore.submitMockSMESKChatFn?.('This feature is coming soon!')}
//           />
//         }
//       />
//     </Grid.Col>
//   </>
// )}
